// The Warga page's list: status filters with their counts, search, and the
// order rows are shown in. Pure functions, so they're cheap to test.

import type { PublicResident } from '@residenone/shared';
import { unitAddress } from './unit-address';

export type ResidentFilter = 'all' | PublicResident['status'];

/** "A No. 1", or null for someone not linked to a unit yet. */
export function residentUnit(r: PublicResident): string | null {
  return r.unit_block !== null && r.unit_number !== null ? unitAddress(r.unit_block, r.unit_number) : null;
}

export function residentCounts(residents: PublicResident[]): Record<ResidentFilter, number> {
  const count = (s: PublicResident['status']) => residents.filter((r) => r.status === s).length;
  return { all: residents.length, ACTIVE: count('ACTIVE'), PENDING: count('PENDING'), SUSPENDED: count('SUSPENDED') };
}

/** Name, email, or unit however it's typed ("A-1", "a 1", "A1"). */
export function matchesResident(r: PublicResident, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  if (r.full_name.toLowerCase().includes(q) || r.email.toLowerCase().includes(q)) return true;
  if (r.unit_block === null || r.unit_number === null) return false;
  return `${r.unit_block}${r.unit_number}`.toLowerCase() === q.replace(/[\s-]/g, '');
}

/**
 * By unit (block, then number), so a household sits together with its primary
 * resident first; people not linked to a unit yet come last, newest first.
 */
export function sortResidents(residents: PublicResident[]): PublicResident[] {
  return [...residents].sort((a, b) => {
    const aLinked = a.unit_block !== null;
    const bLinked = b.unit_block !== null;
    if (aLinked !== bLinked) return aLinked ? -1 : 1;
    if (!aLinked) return b.created_at.localeCompare(a.created_at);
    return (
      a.unit_block!.localeCompare(b.unit_block!, undefined, { numeric: true }) ||
      a.unit_number!.localeCompare(b.unit_number!, undefined, { numeric: true }) ||
      Number(b.is_primary ?? false) - Number(a.is_primary ?? false) ||
      a.full_name.localeCompare(b.full_name)
    );
  });
}
